import { NgIf } from '@angular/common';
import { Component, HostBinding, inject, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { MatMiniFabButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { Dokument, Person } from '@gsm/entities';
import { DYNAMIC_FORM_CONTROL_TYPE_GROUP, DynamicFormControlEvent, DynamicFormGroupModel, DynamicFormModel, DynamicFormValueControlModel, DynamicFormValueControlModelConfig, DynamicSelectModel } from '@ng-dynamic-forms/core';
import { DynamicMaterialFormComponent } from '@ng-dynamic-forms/ui-material';
import debounce from 'lodash/debounce';
import partition from 'lodash/partition';
import remove from 'lodash/remove';
import { CustomDynamicFormService } from '../../custom-dynamic-form.service';
import { setModelDisabled } from '../../helpers/disable-model.helper';
import { getDisplayName } from '../../helpers/display-name.helper';
import { filterEmptyProps } from '../../helpers/filter-props.helper';
import { formElementsInvalid } from '../../helpers/form-elements-invalid.helper';
import { getFormControlName } from '../../helpers/get-form-control-name.helper';
import { getGroupValue } from '../../helpers/get-group-value.helper';
import { getDynamicFormModelMap } from '../../helpers/model-map.helper';
import { CustomDynamicFormControlComponent } from '../custom-dynamic-form-control.component';
import { AnyFormControlModel } from '../shared-models/any-form-control.model';
import { UploadDokument } from '../shared-models/upload-dokument.model';
import { DokumentPickerDialogComponent, DokumentPickerDialogData } from './dokument-picker-dialog/dokument-picker-dialog.component';
import { DOKUMENTEN_OPTIONAL_TEMPLATE_FACTORY, DOKUMENTEN_REQUIRED_TEMPLATE_FACTORY } from './dokumenten-angabe.config';
import { DokumentenAngabeModel, DokumentItem } from './dokumenten-angabe.model';

@Component({
  selector:      'gsm-dokumenten-angabe',
  templateUrl:   './dokumenten-angabe.component.html',
  styleUrls:     ['./dokumenten-angabe.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone:    true,
  imports:       [
    NgIf,
    MatMiniFabButton,
    MatIcon,
    DynamicMaterialFormComponent
  ]
})
export class DokumentenAngabeComponent extends CustomDynamicFormControlComponent<DokumentenAngabeModel> implements OnInit {

  private formService = inject(CustomDynamicFormService);
  dialog = inject(MatDialog);

  @HostBinding('attr.id')
  get id(): string {
    return this.model?.id;
  }

  @ViewChild('requiredForm', {static: true}) requiredFormComponent: DynamicMaterialFormComponent;
  @ViewChild('optionalForm', {static: true}) optionalFormComponent: DynamicMaterialFormComponent;

  requiredFormModel: DynamicFormGroupModel[];
  optionalFormModel: DynamicFormGroupModel[];
  requiredFormGroup: UntypedFormGroup;
  optionalFormGroup: UntypedFormGroup;
  requiredModelTemplate: AnyFormControlModel[];
  optionalModelTemplate: AnyFormControlModel[];

  onFormChange = debounce(this._onFormChange, 200);

  private _isValueUpdating = false;

  constructor() {
    super();
  }

  ngOnInit() {
    super.ngOnInit();
  }

  protected onModelChange() {
    this.requiredModelTemplate = DOKUMENTEN_REQUIRED_TEMPLATE_FACTORY(this.model.additional);
    this.optionalModelTemplate = DOKUMENTEN_OPTIONAL_TEMPLATE_FACTORY(this.model.additional);

    // following initializes required and optional forms/groups
    this.onValueChange();

    if (this.model.disabled) {
      this._setFormsDisabled(true);
    }

    // subscribe to changes to model.additional
    this.model.additionalChange$.pipe(this.untilNextModel)
      .subscribe((prop: string) => this._onAdditionalChange(prop));

    // notify form of new value to ensure proper validation
    this._onFormChange();
  }

  protected onDisabledChange(): void {
    this._setFormsDisabled(this.model.disabled);
  }

  protected onValueChange(): void {
    // if value update was triggered internally, ignore it and reset flag - otherwise an endless loop is set in motion 🤪
    if (this._isValueUpdating) {
      this._isValueUpdating = false;
    } else {
      this._isValueUpdating = true;
      this._updateFormModelsFromValue();
    }
  }

  private _onAdditionalChange(prop: string) {
    // Ensure we have the newest version of model.value.
    this._updateValueFromForm();

    // Filter out any gewerke that aren't present in additional.gewerke.
    if (prop === 'gewerke') {
      this.model.value.forEach(doc => {
        if (doc.gewerke) {
          doc.gewerke = doc.gewerke.filter(dokGewerk => this.model.additional.gewerke.some(gewerk => gewerk.value === dokGewerk.value));
        }
      });
    }

    // If any additional property changes, set the model to itself, in order to re-render everything.
    // Note that this is done instead of just calling this.onModelChange(),
    // because otherwise this.untilNextModel wouldn't work, and we'd end up with
    // multiple subscriptions to additionalChange$.
    this.model = this.model;
  }

  private _updateFormModelsFromValue() {
    const [requiredDoks, optionalDoks] = partition(
      (this.model.value || []).map(doc => filterEmptyProps(doc)),
      doc => doc.required
    );

    // Add required documents to the required documents model.
    const requiredFormModelJson = requiredDoks.map((dok, index) => {
      // create new document model as clone of required model template
      const newDocModel = JSON.parse(JSON.stringify(this.requiredModelTemplate));

      // get model in a KV-map of IDs to control models
      const mappedModel = getDynamicFormModelMap(newDocModel as DynamicFormValueControlModel<any>[]);

      // fill model with document properties
      this._fillModelWithDocumentProperties(dok, mappedModel);

      // https://issues.nv.at/ticketing/anforderungen/-/issues/697
      // Remove NICHT VORHANDEN from Options-Array only for Type VOLLMEDI
      if (dok.typ.value === 'VOLLMEDI') {
        const vorhandenField = newDocModel.find(type => type.id === 'vorhanden');
        const nichtVorhandenOption = vorhandenField.options.find(opt => opt.value === 'NV');
        const i = vorhandenField.options.indexOf(nichtVorhandenOption, 0);
        if (i > -1) {
          vorhandenField.options.splice(i, 1);
        }
      }

      // If updateClaim and document contains gewerke, gewerke field should be disabled and instructions hidden.
      const {
        gewerke,
        gewerke_instructionText
      } = mappedModel;
      if (this.model.additional?.updateClaim && gewerke.value?.length) {
        gewerke.disabled = true;
        gewerke_instructionText.relations = [];
        gewerke_instructionText.hidden = true;
      }

      return {
        type:  DYNAMIC_FORM_CONTROL_TYPE_GROUP,
        id:    'REQUIRED_DOK_' + index,
        group: newDocModel
      };
    });

    this.requiredFormModel = this.formService.fromJSON(requiredFormModelJson) as DynamicFormGroupModel[];
    this.requiredFormGroup = this.formService.createFormGroup(this.requiredFormModel);

    // Add optional documents to the optional documents model.
    const optionalFormModelJson = optionalDoks.map((dok, index) => {
      // create new document model as clone of optional model template
      const newDokModel = JSON.parse(JSON.stringify(this.optionalModelTemplate));

      // get model in a KV-map of IDs to control models
      const mappedModel = getDynamicFormModelMap(newDokModel as DynamicFormValueControlModel<any>[]);

      // fill model with document properties
      this._fillModelWithDocumentProperties(dok, mappedModel);

      return {
        type:  DYNAMIC_FORM_CONTROL_TYPE_GROUP,
        id:    'OPTIONAL_DOK_' + index,
        group: newDokModel
      };
    });

    this.optionalFormModel = this.formService.fromJSON(optionalFormModelJson) as DynamicFormGroupModel[];
    this.optionalFormGroup = this.formService.createFormGroup(this.optionalFormModel);

    if (this.model.additional?.immediateValidation) {
      this.requiredFormGroup.markAllAsTouched();
      this.optionalFormGroup.markAllAsTouched();
    }

    this.formService.detectChanges(this.requiredFormComponent);
    this.formService.detectChanges(this.optionalFormComponent);
  }

  private _getNextDocumentIndex(): number {
    return Math.max(-1, ...this.optionalFormModel.map(model => parseInt(model.id.substring(model.id.lastIndexOf('_') + 1), 10))) + 1;
  }

  addDocument() {
    const newGroup = {
      type:  DYNAMIC_FORM_CONTROL_TYPE_GROUP,
      id:    'OPTIONAL_DOK_' + this._getNextDocumentIndex(),
      group: this.optionalModelTemplate
    };
    const newModel = this.formService.fromJSON([newGroup]);
    this.formService.addFormGroupControl(this.optionalFormGroup, this.optionalFormModel, ...newModel);
    this.formService.detectChanges(this.optionalFormComponent);
    this._onFormChange();
  }

  onMultipleDocumentsClick() {
    this.dialog
      .open<DokumentPickerDialogComponent, DokumentPickerDialogData, UploadDokument[]>(DokumentPickerDialogComponent, {
        minWidth: '600px',
        data:     {
          // optionalTypes should include required types, so that's all we need here
          documentTypes:     this.model.additional.optionalTypes || [],
          acceptedFileTypes: this.model.additional.acceptedFileTypes
        }
      })
      .afterClosed()
      .subscribe(result => {
        if (result) {
          this._addMultipleDocuments(result);
        }
      });
  }

  private _onFormChange() {
    this._updateValueFromForm();
    this.group.controls[this.model.id].setErrors(this._getErrors());
    this.change.emit(this.model.value);
  }

  private _updateValueFromForm() {
    // when setting value, set _isValueUpdating flag to prevent triggering value change logic in an endless loop
    this._isValueUpdating = true;
    this.model.value = this._getTransformedModel();
  }

  private _getTransformedModel(): DokumentItem[] {
    return [
      ...this.requiredFormModel,
      ...this.optionalFormModel
    ].map(model => getGroupValue<DokumentItem>(model.group, true));
  }

  private _getErrors(): ValidationErrors {
    const invalid =
      (this.model.required && !(this.model.value && this.model.value.length)) ||
      formElementsInvalid(this.requiredFormGroup, this.requiredFormModel) ||
      formElementsInvalid(this.optionalFormGroup, this.optionalFormModel);

    return invalid ? {dokumentenAngabe: true} : null;
  }

  // Override parent method
  onCustomEvent(event: DynamicFormControlEvent) {
    if (event.type === 'delete') {
      this._onDelete(event);
    } else {
      super.onCustomEvent(event, null, true);
    }
  }

  private _onDelete(event: DynamicFormControlEvent) {
    const group: UntypedFormGroup = event.group;
    const groupName = getFormControlName(group);
    this._deleteDocument(groupName);
    this._updateFormModelsFromValue();
  }

  private _deleteDocument(groupName: string) {
    let index = this._getGroupIndex(groupName, this.requiredFormModel);
    if (index >= 0) {
      // found in required model
      this.formService.removeFormGroupControl(index, this.requiredFormGroup, this.requiredFormModel);
      this.formService.detectChanges(this.requiredFormComponent);
    } else {
      index = this._getGroupIndex(groupName, this.optionalFormModel);
      if (index >= 0) {
        this.formService.removeFormGroupControl(index, this.optionalFormGroup, this.optionalFormModel);
        this.formService.detectChanges(this.optionalFormComponent);
      }
    }
    this._onFormChange();
  }

  private _getGroupIndex(groupName: string, formModel: DynamicFormModel) {
    return formModel.map(item => item.id)
      .findIndex(id => id === groupName);
  }

  private _addMultipleDocuments(uploadDocuments: UploadDokument[]) {
    const [requiredDocuments, optionalDocuments] = partition(uploadDocuments, doc =>
      this.model.additional?.requiredTypes?.some(type => type.value === doc.type.value)
    );

    this._addRequiredDocuments(requiredDocuments);
    this._addOptionalDocuments(optionalDocuments);
    this._onFormChange();
  }

  private _addRequiredDocuments(uploadDocuments: UploadDokument[]) {
    const uncombinedDocs = this._addCombinedFiles(this.requiredFormModel, uploadDocuments);

    this.requiredFormModel.forEach(requiredDocumentModel => {
      // get model in a KV-map of IDs to control models
      const mappedModel = getDynamicFormModelMap(requiredDocumentModel.group as DynamicFormValueControlModelConfig<any>[]);

      const docOfType = uncombinedDocs.find(doc => doc.type.value === mappedModel.typ.value.value);
      // if a docOfType is present and the required Document does not have any files, add docOfType to the required Document
      if (docOfType && (mappedModel.vorhanden.value !== 'V' || !mappedModel.checklistItemFileList.value?.length)) {
        mappedModel.checklistItemFileList.value = [
          {
            fileInstance:     docOfType.file,
            originalFileName: docOfType.file.name
          }
        ];
        mappedModel.vorhanden.value = 'V';

        // remove added document from uncombinedDocs
        remove(uncombinedDocs, docOfType);
      }
    });

    // add all remaining documents as optional
    this._addOptionalDocuments(uncombinedDocs);
  }

  private _addOptionalDocuments(uploadDocuments: UploadDokument[]) {
    const uncombinedDocs = this._addCombinedFiles(this.optionalFormModel, uploadDocuments);

    const nextDocumentIndex = this._getNextDocumentIndex();
    const optionalFormGroups = uncombinedDocs.map((doc: UploadDokument, index) => {
      // create new document model as clone of optional model template
      const newDocModel = JSON.parse(JSON.stringify(this.optionalModelTemplate));

      // get model in a KV-map of IDs to control models
      const mappedModel = getDynamicFormModelMap(newDocModel as DynamicFormValueControlModelConfig<any>[]);

      mappedModel.checklistItemFileList.value = [
        {
          fileInstance:     doc.file,
          originalFileName: doc.file.name
        }
      ];
      mappedModel.typ.value = doc.type;
      mappedModel.vorhanden.value = 'V';

      return {
        type:  DYNAMIC_FORM_CONTROL_TYPE_GROUP,
        id:    'OPTIONAL_DOK_' + (nextDocumentIndex + index),
        group: newDocModel
      };
    });

    const newOptionalModel = this.formService.fromJSON(optionalFormGroups);
    newOptionalModel.forEach(model => {
      this.formService.addFormGroupControl(this.optionalFormGroup, this.optionalFormModel, model);
    });

    this.formService.detectChanges(this.optionalFormComponent);
  }

  private _addCombinedFiles(docModels: DynamicFormGroupModel[], uploadDocuments: UploadDokument[]): UploadDokument[] {
    const uploadDocs = [...uploadDocuments];

    docModels.forEach((docModel: DynamicFormGroupModel) => {
      // get model in a KV-map of IDs to control models
      const mappedModel = getDynamicFormModelMap(docModel.group as DynamicFormValueControlModelConfig<any>[]);
      // If the document type has the flag "combineFiles", add all files of that type to that document
      if (mappedModel.typ.value && mappedModel.typ.value.combineFiles) {
        const docsOfType = uploadDocs.filter(doc => doc.type.value === mappedModel.typ.value.value);

        if (docsOfType.length) {
          mappedModel.checklistItemFileList.value = [
            ...mappedModel.checklistItemFileList.value,
            ...docsOfType.map(doc => ({
              fileInstance:     doc.file,
              originalFileName: doc.file.name
            }))
          ];
          mappedModel.vorhanden.value = 'V';

          remove(uploadDocs, doc => docsOfType.includes(doc));
        }
      }
    });

    return uploadDocs;
  }

  private _setFormsDisabled(disabled: boolean) {
    setModelDisabled(this.requiredFormModel, disabled, {
      excludeIds: [
        'typ',
        'bezeichnung',
        'geschaedigtePerson'
      ],
      // Prevent fields from being enabled on required document models:
      // - gewerke, when in Nachbringen context and value is present.
      excludeModel: (model: AnyFormControlModel) => !!(model.id === 'gewerke' && this.model.additional?.updateClaim && model.value?.length)
    });
    setModelDisabled(this.optionalFormModel, disabled);
  }

  private _fillModelWithDocumentProperties(dok: Dokument, mappedModel: Record<string, AnyFormControlModel>) {
    Object.keys(dok)
      .forEach(key => {
        const model = mappedModel[key];
        if (!model) {
          return;
        }

        model.value = dok[key];

        if (key === 'geschaedigtePerson') {
          // add option for geschaedigtePerson from value
          if (model.value) {
            const {
              vorname,
              nachname
            } = model.value;
            (model as DynamicSelectModel<Person>).options = [
              {
                label: getDisplayName([
                  vorname,
                  nachname
                ]),
                value: model.value
              }
            ];
          }
          // unhide geschaedigtePerson if present
          model.hidden = !model.value;
        } else if (key === 'bezeichnung' && dok.required) {
          // unhide bezeichnung for required documents, if present
          model.hidden = !model.value;
        }
      });
  }
}
